/* Pages register a title and primary actions in the workspace header.
   Standalone pages keep their own heading/buttons when no host is present. */

import React, { createContext, useContext, useEffect, useLayoutEffect } from 'react';

type SetActions = (node: React.ReactNode) => void;

const TopbarActionsContext = createContext<SetActions | null>(null);
const TopbarTitleContext = createContext<SetActions | null>(null);

/** The bar's owner provides its state setter and renders the state. */
export const TopbarActionsProvider = TopbarActionsContext.Provider;
export const TopbarTitleProvider = TopbarTitleContext.Provider;

/** Registers `node` in the top bar while the caller is mounted. Pass a
    memoized node — a fresh element every render would re-register (and so
    re-render the bar) every render. Returns whether a bar is there at all. */
export function useTopbarActions(node: React.ReactNode): boolean {
  const set = useContext(TopbarActionsContext);
  useEffect(() => {
    if (!set) return undefined;
    set(node);
    return () => set(null);
  }, [set, node]);
  return set !== null;
}

/** Supply a memoized heading node, including its ref/tabIndex if needed.
    The host renders that node without adding another heading element. */
export function useTopbarTitle(node: React.ReactNode): boolean {
  const set = useContext(TopbarTitleContext);
  useLayoutEffect(() => {
    if (!set) return undefined;
    set(node);
    return () => set(null);
  }, [set, node]);
  return set !== null;
}
